/**
 * UnitsListPage
 * Gestão de Unidades (posto de trabalho) por empresa.
 */

import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Button, TextField, Chip, Dialog,
  DialogTitle, DialogContent, DialogActions, MenuItem,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';

import { useAcao } from '../../authorization/useVeredito';
import { PageShell, DataTable, type DataTableColumn } from '../../components';
import { ActionButton, InlineFilterBar, InlineSelect } from '../../components/common';
import type { ActiveChip } from '../../hooks/useFilterState';
import { companyService } from '../../services/companyService';
import { unitService } from '../../services/unitService';
import { Unit, UnitStatus, UNIT_STATUS_LABELS } from '../../types/unit';

interface CompanyOption { id: number; name: string; }

const STATUS_OPTIONS = Object.values(UnitStatus).map((status) => ({
  value: status,
  label: UNIT_STATUS_LABELS[status],
}));

const columns: DataTableColumn<Unit>[] = [
  { id: 'name', label: 'Nome', primary: true, render: (unit) => unit.name },
  { id: 'location', label: 'Localização', render: (unit) => unit.location || '—' },
  {
    id: 'status',
    label: 'Status',
    render: (unit) => (
      <Chip
        size="small"
        label={UNIT_STATUS_LABELS[unit.status]}
        color={unit.status === UnitStatus.ACTIVE ? 'success' : 'default'}
        variant="outlined"
      />
    ),
  },
];

interface UnitFormState {
  name: string;
  location: string;
  status: UnitStatus;
}

const emptyForm: UnitFormState = { name: '', location: '', status: UnitStatus.ACTIVE };

const UnitsListPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const editar = useAcao('unit.update', 'Editar');
  const excluir = useAcao('unit.delete', 'Excluir');

  const [companies, setCompanies] = useState<CompanyOption[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<CompanyOption | null>(null);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<UnitStatus | ''>('');
  const [units, setUnits] = useState<Unit[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Unit | null>(null);
  const [form, setForm] = useState<UnitFormState>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Unit | null>(null);

  useEffect(() => {
    companyService.getCompaniesForDropdown()
      .then((list) => {
        setCompanies(list);
        if (list.length === 1) setSelectedCompany(list[0] ?? null);
      })
      .catch(() => enqueueSnackbar('Erro ao carregar empresas', { variant: 'error' }));
  }, [enqueueSnackbar]);

  const loadUnits = useCallback(async (companyId: number, filters: { search: string; status: UnitStatus | '' }) => {
    setLoading(true);
    try {
      // Lista de gestão: todos os status (ativas e inativas) por padrão, diferente do select do Setor
      const result = await unitService.getUnits({
        companyId,
        limit: 100,
        search: filters.search.trim() || undefined,
        filters: { status: filters.status || undefined },
      });
      setUnits(result.data);
      setTotal(result.pagination.total);
    } catch {
      enqueueSnackbar('Erro ao carregar unidades', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    if (selectedCompany) {
      loadUnits(selectedCompany.id, { search, status });
    } else {
      setUnits([]);
      setTotal(0);
    }
  }, [selectedCompany, search, status, loadUnits]);

  const clearFilters = () => {
    setSearch('');
    setStatus('');
  };

  const chips: ActiveChip[] = [];
  if (status) {
    chips.push({
      key: 'status',
      label: 'Status',
      value: UNIT_STATUS_LABELS[status],
      onDelete: () => setStatus(''),
    });
  }

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (unit: Unit) => {
    setEditing(unit);
    setForm({ name: unit.name, location: unit.location ?? '', status: unit.status });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!selectedCompany) return;
    if (!form.name.trim()) {
      enqueueSnackbar('Nome da unidade é obrigatório', { variant: 'warning' });
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await unitService.updateUnit(editing.id, {
          name: form.name.trim(),
          location: form.location.trim() || undefined,
          status: form.status,
        });
        enqueueSnackbar('Unidade atualizada', { variant: 'success' });
      } else {
        await unitService.createUnit({
          companyId: selectedCompany.id,
          name: form.name.trim(),
          location: form.location.trim() || undefined,
          status: form.status,
        });
        enqueueSnackbar('Unidade criada', { variant: 'success' });
      }
      setDialogOpen(false);
      await loadUnits(selectedCompany.id, { search, status });
    } catch (err: unknown) {
      enqueueSnackbar(err instanceof Error ? err.message : 'Erro ao salvar unidade', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget || !selectedCompany) return;
    try {
      await unitService.deleteUnit(deleteTarget.id);
      enqueueSnackbar('Unidade excluída', { variant: 'success' });
      await loadUnits(selectedCompany.id, { search, status });
    } catch (err: unknown) {
      enqueueSnackbar(err instanceof Error ? err.message : 'Erro ao excluir unidade', { variant: 'error' });
    } finally {
      setDeleteTarget(null);
    }
  };

  return (
    <PageShell
      breadcrumbs={[{ label: 'Início', path: '/' }, { label: 'Unidades' }]}
      title="Unidades"
      subtitle="Postos de trabalho de uma empresa (mesmo CNPJ)"
      actions={
        <ActionButton
          acao="unit.create"
          rotulo="Nova Unidade"
          variant="contained"
          startIcon={<AddIcon />}
          disabled={!selectedCompany}
          onClick={openCreate}
        >
          Nova Unidade
        </ActionButton>
      }
    >
      <Box sx={{ mb: 2 }}>
        <InlineFilterBar
          searchPlaceholder="Buscar por endereço/referência..."
          searchValue={search}
          onSearchChange={setSearch}
          resultCount={selectedCompany ? total : undefined}
          resultNoun="unidades"
          chips={chips}
          onClearAll={clearFilters}
          loading={loading}
        >
          <InlineSelect
            label="Empresa"
            value={selectedCompany ? String(selectedCompany.id) : ''}
            options={companies.map((c) => ({ value: String(c.id), label: c.name }))}
            onChange={(v) => setSelectedCompany(companies.find((c) => String(c.id) === v) ?? null)}
            emptyLabel="Selecione a empresa"
            minWidth={200}
            maxWidth={320}
          />
          <InlineSelect
            label="Status"
            value={status}
            options={STATUS_OPTIONS}
            onChange={(v) => setStatus(v as UnitStatus | '')}
            emptyLabel="Todos os status"
            disabled={!selectedCompany}
            minWidth={160}
          />
        </InlineFilterBar>
      </Box>

      <DataTable
        columns={columns}
        rows={units}
        getRowId={(unit) => unit.id}
        loading={loading}
        emptyState={
          selectedCompany
            ? {
                title: 'Nenhuma unidade cadastrada ainda',
                description:
                  'Unidades são os endereços onde a empresa opera, usados para localizar funcionários e exames.',
                prereqChain: 'units',
                action: {
                  label: 'Cadastrar unidade',
                  node: 'units',
                  deniedHint: 'Peça ao administrador da empresa para cadastrar uma unidade.',
                },
              }
            : {
                title: 'Selecione uma empresa para ver as unidades',
                description:
                  'As unidades são os endereços onde a empresa opera; escolha uma empresa no filtro acima.',
              }
        }
        filtersActive={search.trim() !== '' || status !== ''}
        onClearFilters={clearFilters}
        actions={[
          {
            icon: <EditIcon fontSize="small" />,
            onClick: openEdit,
            ...editar,
          },
          {
            icon: <DeleteIcon fontSize="small" />,
            color: 'error',
            onClick: (unit) => setDeleteTarget(unit),
            ...excluir,
          },
        ]}
      />

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editing ? 'Editar Unidade' : 'Nova Unidade'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Nome" fullWidth required size="small" value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            />
            <TextField
              label="Localização" fullWidth size="small" value={form.location}
              placeholder="Endereço / referência do posto"
              onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
            />
            <TextField
              label="Status" select fullWidth size="small" value={form.status}
              onChange={(e) => setForm((f) => ({ ...f, status: e.target.value as UnitStatus }))}
            >
              {Object.values(UnitStatus).map((s) => (
                <MenuItem key={s} value={s}>{UNIT_STATUS_LABELS[s]}</MenuItem>
              ))}
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Salvando…' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteTarget != null} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Excluir unidade</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja excluir &quot;{deleteTarget?.name}&quot;? Setores e funcionários
            vinculados ficarão sem unidade.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancelar</Button>
          <Button color="error" variant="contained" onClick={confirmDelete}>Excluir</Button>
        </DialogActions>
      </Dialog>
    </PageShell>
  );
};

export default UnitsListPage;
